import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/common/Icon';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { SearchableSelect } from '@/components/ui/searchableSelect';
import { FieldError } from '@/components/ui/fieldError';
import { DatePicker } from '@/components/ui/datePicker';
import { TimePicker } from '@/components/ui/timePicker';
import { ModalOverlay } from '@/components/ui/modalOverlay';
import { isEndTimeAfterStartTime } from '@/utils/formValidation';
import { useSaveAppointmentMutation } from '@/hooks/useSaveAppointmentMutation';
import { useCancelAppointmentMutation } from '@/hooks/useCancelAppointmentMutation';
import { useWorkspacePatientsQuery } from '@/hooks/usePatientsQueries';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { SEARCH_DEBOUNCE_MILLISECONDS } from '@/constants/adminPagination';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import { resolveApiErrorTranslationKey } from '@/api/resolveApiErrorTranslationKey';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import {
  APPOINTMENT_STATUS_CODE_OPTIONS,
  DEFAULT_APPOINTMENT_STATUS_CODE,
  DEFAULT_THERAPY_TYPE_CODE,
  THERAPY_TYPE_CODE_OPTIONS,
  buildAppointmentStatusTranslationKey,
  buildTherapyTypeTranslationKey,
} from '@/constants/appointmentOptions';
import { formatDateToIsoDateOnly } from '@/utils/dateFormatting';
import { computeDraftClashes } from '../utils/externalEvents';
import type { Appointment, AppointmentDraft } from '@/api/appointments/appointmentsApi.types';
import type { PatientSummary } from '@/api/patients/patientsApi.types';
import type { AppointmentModalProps } from '../types';

const DEFAULT_APPOINTMENT_START_TIME = '10:00';
const DEFAULT_APPOINTMENT_END_TIME = '11:00';

type AppointmentValidationErrors = Partial<Record<keyof AppointmentDraft, boolean>>;

interface PatientSelectOption {
  id: string;
  label: string;
}

function buildPatientSelectOptions(
  loadedPatients: PatientSummary[],
  selectedPatientOption: PatientSelectOption | null
): PatientSelectOption[] {
  const options = loadedPatients.map(function toOption(patient) {
    const breedSuffix = patient.breed === null ? '' : ' · ' + patient.breed;
    return { id: patient.id, label: patient.name + breedSuffix };
  });
  if (selectedPatientOption === null) {
    return options;
  }
  const selectedPatientIsListed = options.some(function matchesSelectedPatient(option) {
    return option.id === selectedPatientOption.id;
  });
  if (selectedPatientIsListed === true) {
    return options;
  }
  return [selectedPatientOption, ...options];
}

function buildInitialSelectedPatientOption(
  appointmentToEdit: Appointment | null
): PatientSelectOption | null {
  if (appointmentToEdit === null) {
    return null;
  }
  return { id: appointmentToEdit.patientId, label: appointmentToEdit.patientName };
}

function buildInitialAppointmentDraft(
  appointmentToEdit: Appointment | null,
  initialDate: Date
): AppointmentDraft {
  if (appointmentToEdit === null) {
    return {
      date: formatDateToIsoDateOnly(initialDate),
      startTime: DEFAULT_APPOINTMENT_START_TIME,
      endTime: DEFAULT_APPOINTMENT_END_TIME,
      patientId: '',
      therapyType: DEFAULT_THERAPY_TYPE_CODE,
      status: DEFAULT_APPOINTMENT_STATUS_CODE,
      note: '',
    };
  }
  return {
    date: appointmentToEdit.date,
    startTime: appointmentToEdit.startTime,
    endTime: appointmentToEdit.endTime,
    patientId: appointmentToEdit.patientId,
    therapyType: appointmentToEdit.therapyType,
    status: appointmentToEdit.status,
    note: appointmentToEdit.note,
  };
}

function validateAppointmentDraft(appointmentDraft: AppointmentDraft): AppointmentValidationErrors {
  const validationErrors: AppointmentValidationErrors = {};
  if (appointmentDraft.date.length === 0) {
    validationErrors.date = true;
  }
  if (appointmentDraft.startTime.length === 0) {
    validationErrors.startTime = true;
  }
  if (appointmentDraft.endTime.length === 0) {
    validationErrors.endTime = true;
  } else if (isEndTimeAfterStartTime(appointmentDraft.startTime, appointmentDraft.endTime) === false) {
    validationErrors.endTime = true;
  }
  if (appointmentDraft.patientId.length === 0) {
    validationErrors.patientId = true;
  }
  if (appointmentDraft.therapyType.length === 0) {
    validationErrors.therapyType = true;
  }
  if (appointmentDraft.status.length === 0) {
    validationErrors.status = true;
  }
  return validationErrors;
}

export function AppointmentModal({
  appointmentToEdit,
  initialDate,
  appointments,
  externalEvents,
  onClose,
}: AppointmentModalProps) {
  const { t } = useTranslation();
  const isEditing = appointmentToEdit !== null;

  const [appointmentDraft, setAppointmentDraft] = useState<AppointmentDraft>(
    function initializeDraft() {
      return buildInitialAppointmentDraft(appointmentToEdit, initialDate);
    }
  );
  const [validationErrors, setValidationErrors] = useState<AppointmentValidationErrors>({});
  const [formErrorMessageKey, setFormErrorMessageKey] = useState('');
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false);

  const [selectedPatientOption, setSelectedPatientOption] = useState<PatientSelectOption | null>(
    function initializeSelectedPatient() {
      return buildInitialSelectedPatientOption(appointmentToEdit);
    }
  );
  const [patientSearchInput, setPatientSearchInput] = useState('');
  const debouncedPatientSearch = useDebouncedValue(
    patientSearchInput,
    SEARCH_DEBOUNCE_MILLISECONDS
  );

  const patientsQuery = useWorkspacePatientsQuery(1, debouncedPatientSearch);
  const loadedPatients = patientsQuery.data === undefined ? [] : patientsQuery.data.items;
  const patientSelectOptions = buildPatientSelectOptions(loadedPatients, selectedPatientOption);

  const saveAppointmentMutation = useSaveAppointmentMutation();
  const cancelAppointmentMutation = useCancelAppointmentMutation();
  const isSaving = saveAppointmentMutation.isPending;
  const isCancelling = cancelAppointmentMutation.isPending;
  const isBusy = isSaving || isCancelling;

  const scheduleClashes = computeDraftClashes(
    appointmentDraft,
    appointments,
    externalEvents,
    appointmentToEdit === null ? null : appointmentToEdit.id
  );

  function updateDraftField(fieldName: keyof AppointmentDraft, fieldValue: string) {
    setAppointmentDraft(function computeNextDraft(previousDraft) {
      return { ...previousDraft, [fieldName]: fieldValue };
    });
    setValidationErrors(function clearFieldError(previousErrors) {
      return { ...previousErrors, [fieldName]: false };
    });
  }

  function handleSubmit(submitEvent: FormEvent<HTMLFormElement>) {
    submitEvent.preventDefault();
    const nextValidationErrors = validateAppointmentDraft(appointmentDraft);
    setValidationErrors(nextValidationErrors);
    const errorKeys = Object.keys(nextValidationErrors);
    if (errorKeys.length > 0) {
      const hasTimeOrderIssue =
        appointmentDraft.endTime.length > 0 &&
        isEndTimeAfterStartTime(appointmentDraft.startTime, appointmentDraft.endTime) === false;
      const hasOnlyTimeOrderIssue =
        hasTimeOrderIssue && errorKeys.length === 1 && errorKeys[0] === 'endTime';
      setFormErrorMessageKey(
        hasOnlyTimeOrderIssue ? 'validation:endTimeAfterStart' : 'validation:requiredFieldsMessage'
      );
      return;
    }
    setFormErrorMessageKey('');
    const appointmentIdentifier = appointmentToEdit === null ? null : appointmentToEdit.id;
    saveAppointmentMutation.mutate(
      { appointmentIdentifier, appointmentDraft },
      {
        onSuccess: function notifyAndClose() {
          const successMessageKey = isEditing
            ? 'appointments:notifications.changesSaved'
            : 'appointments:notifications.saved';
          showSuccessNotification(t(successMessageKey));
          onClose();
        },
        onError: function notifyError(saveError) {
          const backendMessage = resolveApiErrorMessage(saveError);
          showErrorNotification(
            backendMessage.length > 0 ? backendMessage : t(resolveApiErrorTranslationKey(saveError))
          );
        },
      }
    );
  }

  function handleConfirmCancel() {
    if (appointmentToEdit === null) {
      return;
    }
    cancelAppointmentMutation.mutate(appointmentToEdit.id, {
      onSuccess: function notifyAndClose() {
        showSuccessNotification(t('appointments:notifications.cancelled'));
        onClose();
      },
      onError: function notifyError(cancelError) {
        showErrorNotification(t(resolveApiErrorTranslationKey(cancelError)));
        setIsCancelConfirmationOpen(false);
      },
    });
  }

  return (
    <ModalOverlay onRequestClose={onClose}>
      <div className="bg-zoowhite w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200 flex flex-col max-h-[90vh]">
        <div className="p-6 border-b border-zooborder flex justify-between items-center bg-zoovpalelavender bg-opacity-30 flex-shrink-0">
          <h3 className="text-xl font-bold text-zooindigo">
            {isEditing ? t('appointments:modal.editTitle') : t('appointments:modal.addTitle')}
          </h3>
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('common:actions.close')}
            onClick={onClose}
          >
            <Icon name="X" className="text-zooindigo" />
          </Button>
        </div>

        <div className="overflow-y-auto p-6 space-y-4 custom-scrollbar">
          {formErrorMessageKey.length > 0 && (
            <div className="p-4 bg-zooerrorbg border border-zooerrorborder rounded-xl flex items-center gap-3 animate-in fade-in slide-in-from-top-2 duration-200">
              <Icon name="AlertCircle" className="text-zooerrortext text-xl" />
              <p className="text-zooerrortext text-sm font-bold">{t(formErrorMessageKey)}</p>
            </div>
          )}

          {scheduleClashes.length > 0 ? (
            <div className="p-4 bg-zoowarningbg border border-zoowarningtext rounded-xl flex gap-3 animate-in fade-in slide-in-from-top-2 duration-200">
              <Icon name="AlertCircle" className="text-zoowarningtext text-xl flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-zoowarningtext text-sm font-bold">
                  {t('calendar:clash.title')}
                </p>
                <ul className="mt-1 space-y-0.5">
                  {scheduleClashes.map(function renderClash(clash, clashIndex) {
                    return (
                      <li
                        key={clash.kind + '-' + String(clashIndex)}
                        className="text-[13px] text-zoowarningtext truncate"
                      >
                        {clash.label} · {clash.startTime}–{clash.endTime}
                        {clash.kind === 'external' ? ' · ' + t('calendar:clash.googleLabel') : ''}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          ) : null}

          <form id="appointment-form" onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div className="space-y-1 col-span-2 sm:col-span-1">
                <Label className="flex items-center gap-1 min-h-[16px]">
                  {t('appointments:fields.date')} <span className="text-zooerrortext">*</span>
                </Label>
                <DatePicker
                  id="appointment-date"
                  value={appointmentDraft.date}
                  hasError={validationErrors.date === true}
                  onChange={function updateDate(nextDate) {
                    updateDraftField('date', nextDate);
                  }}
                />
              </div>
              <div className="space-y-1">
                <Label className="flex items-center gap-1 min-h-[16px]">
                  {t('appointments:fields.from')} <span className="text-zooerrortext">*</span>
                </Label>
                <TimePicker
                  id="appointment-from"
                  value={appointmentDraft.startTime}
                  hasError={validationErrors.startTime === true}
                  onChange={function updateStartTime(nextTime) {
                    updateDraftField('startTime', nextTime);
                  }}
                />
              </div>
              <div className="space-y-1">
                <Label className="flex items-center gap-1 min-h-[16px]">
                  {t('appointments:fields.to')} <span className="text-zooerrortext">*</span>
                </Label>
                <TimePicker
                  id="appointment-to"
                  value={appointmentDraft.endTime}
                  hasError={validationErrors.endTime === true}
                  onChange={function updateEndTime(nextTime) {
                    updateDraftField('endTime', nextTime);
                  }}
                />
              </div>
            </div>
            {validationErrors.endTime === true &&
            appointmentDraft.endTime.length > 0 &&
            isEndTimeAfterStartTime(appointmentDraft.startTime, appointmentDraft.endTime) ===
              false ? (
              <FieldError messageKey="validation:endTimeAfterStart" />
            ) : null}

            <div className="space-y-1">
              <Label className="flex items-center gap-1 min-h-[16px]">
                {t('appointments:fields.patient')} <span className="text-zooerrortext">*</span>
              </Label>
              <SearchableSelect
                inputId="appointment-patient"
                value={appointmentDraft.patientId}
                hasError={validationErrors.patientId === true}
                isLoading={patientsQuery.isFetching}
                placeholder={t('appointments:placeholders.selectPatient')}
                options={patientSelectOptions.map(function toSelectOption(patientOption) {
                  return { value: patientOption.id, label: patientOption.label };
                })}
                disableBuiltInFilter
                onInputChange={setPatientSearchInput}
                onChange={function updatePatient(nextValue) {
                  const chosenPatientOption = patientSelectOptions.find(function matches(option) {
                    return option.id === nextValue;
                  });
                  setSelectedPatientOption(
                    chosenPatientOption === undefined ? null : chosenPatientOption
                  );
                  updateDraftField('patientId', nextValue);
                }}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="flex items-center gap-1 min-h-[16px]">
                  {t('appointments:fields.therapyType')} <span className="text-zooerrortext">*</span>
                </Label>
                <SearchableSelect
                  inputId="appointment-therapy-type"
                  value={appointmentDraft.therapyType}
                  hasError={validationErrors.therapyType === true}
                  placeholder={t('appointments:placeholders.selectType')}
                  options={THERAPY_TYPE_CODE_OPTIONS.map(function toSelectOption(therapyTypeCode) {
                    return {
                      value: therapyTypeCode,
                      label: t(buildTherapyTypeTranslationKey(therapyTypeCode)),
                    };
                  })}
                  onChange={function updateTherapyType(nextValue) {
                    updateDraftField('therapyType', nextValue);
                  }}
                />
              </div>
              <div className="space-y-1">
                <Label className="flex items-center gap-1 min-h-[16px]">
                  {t('appointments:fields.status')} <span className="text-zooerrortext">*</span>
                </Label>
                <SearchableSelect
                  inputId="appointment-status"
                  value={appointmentDraft.status}
                  hasError={validationErrors.status === true}
                  options={APPOINTMENT_STATUS_CODE_OPTIONS.map(function toSelectOption(statusCode) {
                    return {
                      value: statusCode,
                      label: t(buildAppointmentStatusTranslationKey(statusCode)),
                    };
                  })}
                  onChange={function updateStatus(nextValue) {
                    updateDraftField('status', nextValue);
                  }}
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label className="min-h-[16px] block">{t('appointments:fields.note')}</Label>
              <Textarea
                rows={3}
                placeholder={t('appointments:placeholders.note')}
                value={appointmentDraft.note}
                onChange={function updateNote(changeEvent) {
                  updateDraftField('note', changeEvent.target.value);
                }}
              />
            </div>
          </form>
        </div>

        <div className="p-6 border-t border-zooborder flex flex-col-reverse items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between bg-zoowhite flex-shrink-0">
          {isEditing ? (
            <Button
              variant="linkDanger"
              onClick={function openCancelConfirmation() {
                setIsCancelConfirmationOpen(true);
              }}
              disabled={isBusy}
              className="text-sm flex items-center justify-center gap-1 whitespace-nowrap"
            >
              <Icon name="X" /> {t('appointments:modal.cancelAppointment')}
            </Button>
          ) : (
            <div className="hidden sm:block" />
          )}

          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={onClose} disabled={isBusy}>
              {isEditing ? t('common:actions.close') : t('common:actions.cancel')}
            </Button>
            <Button
              form="appointment-form"
              type="submit"
              disabled={isBusy}
              isLoading={isSaving}
              className="flex items-center gap-2"
            >
              {isSaving
                ? t('appointments:modal.saving')
                : isEditing
                  ? t('common:actions.saveChanges')
                  : t('appointments:modal.saveAppointment')}
            </Button>
          </div>
        </div>
      </div>

      {isCancelConfirmationOpen && (
        <ModalOverlay
          onRequestClose={function closeCancelConfirmation() {
            setIsCancelConfirmationOpen(false);
          }}
          className="bg-opacity-80 backdrop-blur-md z-[60]"
        >
          <div className="bg-zoowhite max-w-sm w-full rounded-3xl p-8 shadow-2xl text-center">
            <div className="w-16 h-16 bg-zooerrorbg rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="X" className="text-3xl text-zooerrortext" />
            </div>
            <h4 className="text-xl font-bold text-zooindigo mb-2">
              {t('appointments:cancelConfirm.title')}
            </h4>
            <p className="text-zoomuted text-sm mb-8">
              {t('appointments:cancelConfirm.description')}
            </p>
            <div className="flex gap-4">
              <Button
                variant="outline"
                onClick={function closeCancelConfirmation() {
                  setIsCancelConfirmationOpen(false);
                }}
                disabled={isCancelling}
                className="flex-1"
              >
                {t('appointments:cancelConfirm.keep')}
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirmCancel}
                isLoading={isCancelling}
                className="flex-1 flex items-center justify-center gap-2"
              >
                {isCancelling
                  ? t('appointments:modal.cancelling')
                  : t('appointments:modal.cancelAppointment')}
              </Button>
            </div>
          </div>
        </ModalOverlay>
      )}
    </ModalOverlay>
  );
}
